import { CheckIcon } from "@/components/common/Icons";
import { Button } from "@/components/ui/Button";

/**
 * HeroSection Component
 * Main hero section with animated background and CTA
 */
export function HeroSection() {
  const benefits = [
    "Most people battling weight have gut trouble",
    "Boost metabolism & curb cravings",
    "Try the gut reset thousands swear by",
    "Lasting weight loss results",
  ];

  const heroSectionBgDesktop = `${window.template_root}/src/public/img/marquee-desk.webp`;
  const heroSectionBgMobile = `${window.template_root}/src/public/img/marquee-mob.webp`;

  const ctaLink = window.cta_link || "#";
  const acfData = window.post_template || [];
  const showDirectCheckoutLink = acfData.show_direct_checkout_cta || false;
  const directCheckoutLink = acfData.direct_checkout_link || "#";
  const directCheckoutLabel = acfData.tr_direct_cta_label;

  return (
    <section
      className="relative grid grid-cols-[1fr_1.2fr] max-w-none m-0 w-full min-h-[85vh]
                        bg-dark-green hero-gradient bg-cover bg-center bg-no-repeat
                        py-0 pl-20 pr-12 gap-8 rounded-none
                        max-md:flex max-md:flex-col max-md:p-0 max-md:min-h-auto max-md:gap-0
                        max-[1000px]:px-4 max-[1000px]:pl-8"
    >
      {/* Text Container */}
      <div
        className="relative z-[2] max-w-[480px] text-white flex flex-col gap-5 items-start text-left
                      md:w-[clamp(0px,100%,calc(100vw-4rem))] self-center
                      max-md:p-5 max-md:max-w-full max-md:gap-3 max-md:order-2 max-md:flex-1"
      >
        {/* Customers Badge */}
        <div
          className="bg-white/5 border border-white/[0.03] rounded-[88rem] py-2 px-5 pr-5 pl-2 w-fit
                        opacity-0 animate-fade-in-up [animation-delay:0.2s]
                        max-md:py-1.5 max-md:pr-3.5 max-md:pl-1.5 max-md:mb-0.5"
        >
          <div className="flex items-center gap-2.5 max-md:gap-2">
            <img
              src={`${window.template_root}/src/public/img/hero-users.webp`}
              width="80"
              height="32"
              alt="Customer avatars"
              className="block h-6 w-auto max-md:w-[50px] max-md:h-auto"
            />
            <p className="text-white text-sm m-0 whitespace-nowrap max-md:text-[0.85rem]">
              <b className="font-semibold">129,755</b> happy customers
            </p>
          </div>
        </div>

        {/* Title */}
        <h1
          className="font-family-title text-[2.4rem] font-normal leading-tight text-white m-0
                       opacity-0 animate-fade-in-up [animation-delay:0.3s]
                       max-md:text-[1.8rem] max-md:leading-[1.25] max-md:mb-0.5
                       max-[1000px]:text-[2.2rem]"
        >
          Is your gut health holding back your weight loss?
        </h1>

        {/* Benefits List */}
        <ul
          className="list-none p-0 m-0 flex flex-col gap-2 text-white
                       opacity-0 animate-fade-in-up [animation-delay:0.5s]
                       max-md:text-left max-md:gap-1.5 max-md:mb-0"
        >
          {benefits.map((benefit, index) => (
            <li
              key={index}
              className="flex items-start gap-2 text-[1.15rem] font-[450]
                         max-md:text-[1.05rem] max-md:gap-2 max-md:leading-relaxed"
            >
              <CheckIcon
                className="w-[12px] h-[24px] flex-shrink-0 mt-[0.15em]"
                color="var(--color-secondary)"
              />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>

        {/* CTA Buttons */}
        <div
          className="flex gap-4 mt-3
                        opacity-0 animate-fade-in-up [animation-delay:0.7s]
                        max-md:w-full max-md:max-w-full max-md:gap-3 max-md:mt-3"
        >
          <Button
            href={ctaLink}
            variant="primary"
            hero
            className="max-md:w-full max-md:py-3.5 max-md:px-6 max-md:text-base"
          >
            {acfData.tr_cta_text}
          </Button>
          {showDirectCheckoutLink && (
            <Button
              href={directCheckoutLink}
              variant="secondary"
              hero
              className="max-md:w-full max-md:py-3.5 max-md:px-6 max-md:text-base"
            >
              {directCheckoutLabel}
            </Button>
          )}
        </div>
      </div>

      {/* Animated Background Grid */}
      <div
        className="relative w-full h-full overflow-hidden
                      max-md:w-full max-md:h-[180px] max-md:mt-[70px] max-md:min-h-auto max-md:order-1 max-md:flex-shrink-0"
      >
        <div
          className="absolute top-0 left-0 w-full h-[5115px] bg-(image:--heroSectionBgDesktop) bg-repeat-y bg-top
                     bg-[length:100%] animate-marquee-vertical whitespace-nowrap
                     max-md:w-[7980px] max-md:h-full max-md:bg-(image:--heroSectionBgMobile) max-md:bg-repeat-x max-md:bg-left-top
                     max-md:bg-[length:auto_100%] max-md:animate-marquee-horizontal"
          style={{
            "--heroSectionBgDesktop": `url('${heroSectionBgDesktop}')`,
            "--heroSectionBgMobile": `url('${heroSectionBgMobile}')`,
          }}
        />
      </div>
    </section>
  );
}

export default HeroSection;
